import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Loader2, RefreshCw, FileText, AlertTriangle, CheckCircle2, X, Filter } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_COLORS = {
  CREDIT_NEGATIVE:    'bg-green-100 text-green-800 border-green-300',
  EXCESS_BALANCE:     'bg-red-100 text-red-800 border-red-300',
  CUSTOMER_PAID:      'bg-red-100 text-red-800 border-red-300',
  DUPLICATE_REQUEST:  'bg-red-100 text-red-800 border-red-300',
  UNABLE_TO_PAY:      'bg-red-100 text-red-800 border-red-300',
  ACCOUNT_INACTIVE:   'bg-red-100 text-red-800 border-red-300',
  FOLLOW_UP:          'bg-yellow-100 text-yellow-800 border-yellow-300',
  HOLD:               'bg-yellow-100 text-yellow-800 border-yellow-300',
  WAY_HIGH:           'bg-purple-100 text-purple-800 border-purple-300',
  OTHER:              'bg-gray-100 text-gray-800 border-gray-300',
  NO_NOTE:            'bg-orange-100 text-orange-800 border-orange-300',
};

export default function RefundPendingPage({ accounts = [] }) {
  const [summary, setSummary] = useState(null);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [orgFilter, setOrgFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [confirmRefund, setConfirmRefund] = useState(null);
  const limit = 50;

  const loadSummary = async () => {
    setLoadingSummary(true);
    try {
      const params = orgFilter ? `?account_id=${orgFilter}` : '';
      const { data } = await api.get(`/cockpit/refund-pending/summary${params}`);
      setSummary(data);
    } catch (e) {
      toast.error('Failed to load summary');
    } finally {
      setLoadingSummary(false);
    }
  };

  const loadBills = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (orgFilter) params.set('account_id', orgFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      if (search) params.set('search', search);
      params.set('page', String(page));
      params.set('limit', String(limit));
      const { data } = await api.get(`/cockpit/refund-pending/bills?${params}`);
      setBills(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Failed to load bills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSummary(); }, [orgFilter]);
  useEffect(() => { loadBills(); }, [orgFilter, categoryFilter, search, page]);

  const handleSaveNote = async (bill) => {
    if (!noteText.trim()) {
      toast.error('Note cannot be empty');
      return;
    }
    try {
      await api.post('/cockpit/refund-pending/note', {
        bill_id: bill.bill_id,
        payment_file_id: bill.payment_file_id,
        account_id: bill.account_id,
        note: noteText,
      });
      toast.success('Note saved');
      setEditingNote(null);
      setNoteText('');
      loadBills();
    } catch (e) {
      toast.error('Failed to save note');
    }
  };

  const handleMarkRefunded = async (bill) => {
    try {
      await api.post('/cockpit/refund-pending/mark-refunded', {
        bill_id: bill.bill_id,
        payment_file_id: bill.payment_file_id,
        account_id: bill.account_id,
        refund_amount: bill.approved_amount,
        notes: bill.notes || '',
      });
      toast.success('Marked as refunded');
      // Auto-remove the row from the visible list to give immediate feedback
      setBills(prev => prev.filter(b => b.bill_id !== bill.bill_id));
      setConfirmRefund(null);
      loadSummary();
    } catch (e) {
      toast.error('Failed to mark as refunded');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="refund-pending-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Refund Pending Review
          </h2>
          <p className="text-sm text-muted-foreground">
            Bills with allocation gaps that need ops attention or refund processing
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { loadSummary(); loadBills(); }} data-testid="refund-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary cards */}
      {loadingSummary ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : summary ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Total Pending" value={summary.total_pending} icon={FileText} testId="kpi-total" />
            <SummaryCard label="Needs Action" value={summary.needs_action_total} icon={AlertTriangle} testId="kpi-action" />
            <SummaryCard label="$ Pending" value={`$${(summary.total_amount || 0).toLocaleString()}`} icon={FileText} testId="kpi-amount" />
            <SummaryCard label="Categories" value={summary.categories.filter(c => c.bills > 0).length} icon={Filter} testId="kpi-cats" />
          </div>

          {/* Category cards (clickable filter) */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="category-grid">
            {summary.categories.filter(c => c.bills > 0).map((c) => (
              <button
                key={c.category}
                onClick={() => { setCategoryFilter(c.category === categoryFilter ? '' : c.category); setPage(1); }}
                data-testid={`cat-card-${c.category}`}
                className={`text-left rounded border-2 p-3 transition ${
                  categoryFilter === c.category ? 'ring-2 ring-primary' : ''
                } ${CATEGORY_COLORS[c.category] || ''}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase">{c.label}</span>
                  {c.needs_action && <AlertTriangle className="h-3 w-3" />}
                </div>
                <div className="text-2xl font-extrabold mt-1">{c.bills.toLocaleString()}</div>
                <div className="text-xs opacity-75">${c.approved_total.toLocaleString()}</div>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 border-y py-3">
        <Select value={orgFilter || 'all'} onValueChange={(v) => { setOrgFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-56" data-testid="filter-org"><SelectValue placeholder="All Orgs" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Orgs</SelectItem>
            {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.organisation_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          placeholder="Search bill_id, supplier, account #"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-72"
          data-testid="filter-search"
        />
        {categoryFilter && (
          <Badge variant="outline" className="gap-1" data-testid="active-cat-badge">
            {categoryFilter}
            <button onClick={() => setCategoryFilter('')}><X className="h-3 w-3" /></button>
          </Badge>
        )}
        <span className="text-sm text-muted-foreground ml-auto">
          {total.toLocaleString()} bill{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Bill table */}
      <div className="border rounded">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : bills.length === 0 ? (
          <div className="text-center py-12 text-sm text-muted-foreground" data-testid="empty-state">No bills match the current filters.</div>
        ) : (
          <table className="w-full text-sm" data-testid="refund-bills-table">
            <thead className="border-b bg-muted/30">
              <tr className="text-left">
                <th className="p-2">Org</th>
                <th className="p-2">Category</th>
                <th className="p-2">Bill</th>
                <th className="p-2">Supplier</th>
                <th className="p-2 text-right">$ Approved</th>
                <th className="p-2 text-right">$ Paid</th>
                <th className="p-2 text-right">BP / Anchor</th>
                <th className="p-2">Notes</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b) => (
                <tr key={b.bill_id} className="border-b hover:bg-muted/20" data-testid={`bill-row-${b.bill_id}`}>
                  <td className="p-2 text-xs">{b.org}</td>
                  <td className="p-2">
                    <Badge variant="outline" className={CATEGORY_COLORS[b.category] || ''} data-testid={`bill-cat-${b.bill_id}`}>
                      {b.category_label}
                    </Badge>
                  </td>
                  <td className="p-2 font-mono text-xs">
                    <div>{b.bill_id.slice(0, 8)}...</div>
                    <div className="text-muted-foreground">{b.invoice_number}</div>
                  </td>
                  <td className="p-2 text-xs">
                    <div className="font-medium">{b.supplier_name}</div>
                    <div className="text-muted-foreground">{b.account_number}</div>
                  </td>
                  <td className="p-2 text-right font-mono">${b.approved_amount.toLocaleString()}</td>
                  <td className="p-2 text-right font-mono">${b.paid_amount.toLocaleString()}</td>
                  <td className="p-2 text-right font-mono text-xs">
                    BP: ${b.bp_paid.toLocaleString()}<br/>
                    A: ${b.anchor_paid.toLocaleString()}
                  </td>
                  <td className="p-2 text-xs max-w-xs">
                    {editingNote === b.bill_id ? (
                      <div className="flex gap-1">
                        <Input
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          className="h-7 text-xs"
                          autoFocus
                          data-testid={`note-input-${b.bill_id}`}
                        />
                        <Button size="sm" variant="default" className="h-7 px-2" onClick={() => handleSaveNote(b)} data-testid={`note-save-${b.bill_id}`}>
                          <CheckCircle2 className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setEditingNote(null); setNoteText(''); }}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div
                        className="cursor-pointer hover:underline"
                        onClick={() => { setEditingNote(b.bill_id); setNoteText(b.notes || ''); }}
                        data-testid={`note-cell-${b.bill_id}`}
                      >
                        {b.notes || <span className="text-muted-foreground italic">+ Add note</span>}
                      </div>
                    )}
                  </td>
                  <td className="p-2">
                    {b.needs_action && b.category !== 'WAY_HIGH' && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setConfirmRefund(b)} data-testid={`refund-btn-${b.bill_id}`}>
                        Mark Refunded
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm" data-testid="refund-pagination">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)} data-testid="pg-prev">Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="pg-next">Next</Button>
          </div>
        </div>
      )}

      {/* Confirm refund dialog */}
      <AlertDialog open={!!confirmRefund} onOpenChange={(o) => !o && setConfirmRefund(null)}>
        <AlertDialogContent data-testid="confirm-refund-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark this bill as refunded?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmRefund && (
                <>
                  This will mark the bill <span className="font-mono text-xs">{confirmRefund.bill_id.slice(0, 8)}…</span> for{' '}
                  <strong>{confirmRefund.org}</strong> with refund amount <strong>${confirmRefund.approved_amount.toLocaleString()}</strong>.
                  {' '}The bill will be removed from this review list.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-refund-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmRefund && handleMarkRefunded(confirmRefund)} data-testid="confirm-refund-confirm">
              Mark Refunded
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, testId }) {
  return (
    <div className="border rounded p-3" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className="text-2xl font-extrabold mt-1">{value}</div>
    </div>
  );
}
